import { isEmpty, isFunction, isNil, merge, isEqual, get } from 'lodash';

import { WIDGET_COMPONENT } from '@app/shared/types/enums/widget-component.enum';
import { IModalDialogOptions } from '@app/shared/types/ui/modal-options.type';
import {
  BTN_CANCEL,
  BTN_CONTINUE,
} from '@app/text-content/button-templates/button-templates-content';
import {
  PERCENTAGE_SIGN,
  POSITION_RIGHT,
  WIDGET_FAMILY,
} from '@config/global-constants';

import { TRANSFER_REPLENISH_DEFAULT_PERCENT } from '../../transfer/transfer-constants';
import {
  IReplenishConfirmBuilderParams,
  ReplenishConfirmBuilder,
} from './replenish-confirm.types';
import { DEFAULT_REPLENISH_PERCENT } from '../replenish.constants';
import {
  REPLENISH_APPLY_ITEMS_SCOPE,
  REPLENISH_SCOPE_OPTIONS,
  REPLENISH_SCOPE_OPTIONS_VALUES,
} from '../replenish.enums';
import { getReplenishItemOptions } from './builder.utils';
import { IReplenishDestination } from '../replenish.types';
import { FormGroup } from '@angular/forms';
import { rx } from '@app/shared/rx';
import { BehaviorSubject } from 'rxjs';
import { I18n } from '@app/i18n/strings-keys';

const replenishModalBuilder: ReplenishConfirmBuilder = (
  params,
): IModalDialogOptions => {
  const defaultParams: Partial<IReplenishConfirmBuilderParams> = {
    showItemOptions: true,
  };

  const modalParams = merge({}, defaultParams, params);
  const destination = params.replenishSite as IReplenishDestination;
  const disabledReplenishOption: boolean = get(
    destination,
    'disabledReplenishOption',
    false,
  );

  const filtered$: BehaviorSubject<boolean> = new BehaviorSubject(
    !disabledReplenishOption,
  );

  return merge({}, <IModalDialogOptions>{
    title: isEmpty(modalParams.modalTitle)
      ? I18n.Forms.ReplenishForm.ReplenishToPAR
      : modalParams.modalTitle,
    validation: true,
    modalClass: `modal-md hidden-header-checkbox`,
    buttons: [BTN_CONTINUE, BTN_CANCEL],
    schema: {
      type: 'object',
      properties: {
        // TODO XVI-10490 replenish options
        replenishOption: { type: 'string', required: true },
        itemOption: { type: 'string', required: modalParams.showItemOptions },
        percent: {
          type: 'number',
          default: DEFAULT_REPLENISH_PERCENT,
        },
      },
    },
    value: {
      itemOption: REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY,
      replenishOption: disabledReplenishOption
        ? REPLENISH_SCOPE_OPTIONS_VALUES.ALL_ITEMS
        : REPLENISH_SCOPE_OPTIONS_VALUES.FILTERED_LIST,
    },
    modalResultMapper: isFunction(modalParams.modalResultMapper)
      ? modalParams.modalResultMapper
      : (value: {
          itemOption: REPLENISH_APPLY_ITEMS_SCOPE;
          percent: number;
          replenishOption: REPLENISH_SCOPE_OPTIONS_VALUES;
        }) => {
          const { itemOption, percent, replenishOption } = value;

          return {
            replenishOption,
            itemOption,
            items: [
              {
                percent: isNil(percent) ? DEFAULT_REPLENISH_PERCENT : percent,
                fromSite: destination.fromSite,
                toSite: destination.toSite,
                inventory_items: isEqual(
                  replenishOption,
                  REPLENISH_SCOPE_OPTIONS_VALUES.FILTERED_LIST,
                )
                  ? destination.filtered_inventory_items
                  : destination.all_inventory_items,
              },
            ],
          };
        },
    formRules: {
      fields: {
        percent: {
          title: I18n.Forms.ReplenishForm.ReplenishToPercentOfPAR,
          widget: WIDGET_COMPONENT.input_currency,
          widgetFamily: WIDGET_FAMILY.XUX,
          class: 'standard-width',
          minimum: 1,
          maximum: 999,
          widgetData: {
            currencySymbolFn: (): string => PERCENTAGE_SIGN,
            positionFn: (): string => POSITION_RIGHT,
            precisionFn: (): number => 0,
            defaultValueByClearFn: (): number =>
              TRANSFER_REPLENISH_DEFAULT_PERCENT,
          },
        },
        itemOption: getReplenishItemOptions({
          filtered$,
          showItemOptions: modalParams.showItemOptions,
          itemOptionsClass: 'select-full-width max-inline-col-width',
        }),
        replenishOption: {
          title: I18n.Forms.ReplenishForm.ReplenishOptions,
          widget: WIDGET_COMPONENT.select,
          description: modalParams.i18nService.translate(
            I18n.InfoMessages.ReplenishOptionsTooltip,
          ),
          widgetFamily: WIDGET_FAMILY.XUX,
          required: true,
          disabled: disabledReplenishOption,
          selectOptions: REPLENISH_SCOPE_OPTIONS,
        },
      },
    },
    onFormReady: popWindowFormReady(filtered$),
    onFormClose: () => {
      filtered$.next(null);
    },
  });
};

const popWindowFormReady: Function = (filtered$: BehaviorSubject<boolean>) => {
  return (form: FormGroup): void => {
    form.controls.replenishOption.valueChanges
      .pipe(rx.distinctUntilChanged(isEqual))
      .subscribe((data) => {
        filtered$.next(
          isEqual(REPLENISH_SCOPE_OPTIONS_VALUES.FILTERED_LIST, data),
        );
      });
  };
};

export { replenishModalBuilder };
